<script setup lang="ts">
import { computed } from 'vue'
import { useTranslate } from '@tolgee/vue'
import type { IProject, ImpactColor, ImpactType, Impact } from '@/api/interfaces'
import { getCSSVariable, formatTotalAmounts } from '@/helpers/utils'
import Decimal from 'decimal.js'

const { t } = useTranslate()

interface Props {
  project: IProject
  colorSettings: Record<ImpactType, ImpactColor>
  impact?: Impact
  active?: boolean
}

const props = defineProps<Props>()

const deprecated = computed(() => props.project.status === 'deprecated')
const projectHeader = computed(() => {
  return {
    label: t.value(`${props.project.projectId}.name`),
    name: t.value(`${props.project.projectId}.subName`),
  }
})
</script>

<template>
  <v-tooltip :text="t('PublicProjectCard.deprecated_tooltip')" location="top" max-width="220px">
    <template v-slot:activator="{ props }">
      <a
        :href="project.link || undefined"
        target="_blank"
        :class="['project-card', { active }]"
        :style="{
          borderColor:
            active && !deprecated
              ? colorSettings[project.type].backgroundColor
              : getCSSVariable('gray'),
        }"
        v-bind="deprecated ? props : undefined"
      >
        <div class="project-card-content">
          <span
            :class="['project-card-impact-values', { deprecated }]"
            v-if="(deprecated || impact) && $route.name !== 'CommunityPage'"
            :style="{
              color: colorSettings[project.type].textColor,
              backgroundColor:
                impact && !deprecated
                  ? colorSettings[project.type].backgroundColor
                  : getCSSVariable('gray'),
            }"
          >
            <template v-if="deprecated">
              {{ t('PublicProjectCard.deprecated') }}
            </template>
            <span v-if="deprecated && impact"> - </span>
            <template v-if="impact">
              {{
                t(`PublicProjectCard.${impact.type}`, {
                  count: formatTotalAmounts(
                    new Decimal(impact.amount)
                      .dividedBy(impact.type === 'carbon' ? 1000 : 1)
                      .toNumber(),
                  ),
                })
              }}
            </template>
          </span>
          <p
            class="project-card-title"
            :style="{ color: colorSettings[project.type].backgroundColor }"
          >
            {{ projectHeader.label }}
          </p>
          <h3 class="project-card-text">
            {{ projectHeader.name }}
          </h3>
        </div>
        <img
          class="project-card-image"
          :src="project.imageUrl"
          :alt="project.name"
          loading="lazy"
        />
      </a>
    </template>
  </v-tooltip>
</template>

<style lang="scss" scoped>
.project-card {
  background-color: var(--ui-white);
  border-radius: 10px;
  display: flex;
  padding: 16px;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--gray);
  box-shadow: 2px 2px 20px 0 rgba(0 0 0 / 10%);
  text-align: left;
  text-decoration: none;
  max-width: 295px;
  transition: border-color 200ms ease-in-out;

  &:hover {
    border-color: var(--ui-green);
  }
}

.project-card-content {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.project-card-impact-values {
  padding: 4px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  color: var(--ui-black);
}

.project-card-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
}

.project-card-text {
  font-size: 14px;
  font-weight: 700;
  color: var(--ui-black);
}

.project-card-image {
  height: 60px;
  width: 60px;
  border-radius: 8px;
  object-fit: cover;
}
</style>
